import {
  EMBARGO_MODES,
  EMBARGO_SAVE_ERROR_NAME,
  EMBARGO_VALIDATION_TYPES,
} from '../constants';
import { EMBARGO_CONTENT, getEmbargoBannerTitle } from '../content';
import type { EmbargoMode, EmbargoSaveError } from '../types';

const readString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isEmbargoErrorName = (value: unknown): boolean =>
  readString(value).toLowerCase() === EMBARGO_SAVE_ERROR_NAME.toLowerCase();

export type ParseEmbargoSaveErrorOptions = {
  /** Pickup Management flow — used to infer banner title from the active save API. */
  embargoMode?: EmbargoMode;
};

/**
 * Parses backend save-time embargo error from pickup save API failure payload.
 *
 * Backend contract: `{ error: { name: "EmbargoError", message } }` only.
 * Banner title and validationType are assigned on the client.
 */
export const parseEmbargoSaveError = (
  payload: unknown,
  { embargoMode = EMBARGO_MODES.EDIT }: ParseEmbargoSaveErrorOptions = {}
): EmbargoSaveError | null => {
  if (!isRecord(payload) || !isRecord(payload.error)) {
    return null;
  }

  const error = payload.error;
  if (!isEmbargoErrorName(error.name)) {
    return null;
  }

  const message = readString(error.message);
  if (!message) {
    return null;
  }

  return {
    embargoTitle: getEmbargoBannerTitle(embargoMode, EMBARGO_VALIDATION_TYPES.ERROR),
    embargoDescription: message,
    validationType: EMBARGO_VALIDATION_TYPES.ERROR,
  };
};

export type PickupSaveFailureResolution = {
  embargoBackendSaveError: EmbargoSaveError | null;
  shouldSuppressSnackbar: boolean;
};

/** Shared save-failure handling for create/edit/bulk/merge pickup flows. */
export const resolvePickupSaveFailure = ({
  responseData,
  statusCode,
  embargoMode = EMBARGO_MODES.EDIT,
}: {
  responseData: unknown;
  statusCode?: number;
  embargoMode?: EmbargoMode;
}): PickupSaveFailureResolution => {
  const embargoBackendSaveError = parseEmbargoSaveError(responseData, { embargoMode });
  return {
    embargoBackendSaveError,
    shouldSuppressSnackbar:
      statusCode === 403 || Boolean(embargoBackendSaveError),
  };
};
